import type { ComponentBaseProps } from "@/ui/componentBase";
import type React from "react";
import { renderTimeAgo } from "@/components/TimeAgo";
import type { MetadataItem } from "@/ui/metadataList";
import { getBackgroundColorClass } from "@/lib/colors";
import doIcon from "@/assets/icons/integrations/digitalocean.svg";
import { getStateMap } from "..";
import type {
  ComponentBaseContext,
  ComponentBaseMapper,
  ExecutionDetailsContext,
  NodeInfo,
  OutputPayload,
  SubtitleContext,
} from "../types";
import type { DatabaseNodeMetadata, GetDatabaseConfiguration } from "./types";
import { baseEventSections } from "./event_helpers";

export const getDatabaseMapper: ComponentBaseMapper = {
  props(context: ComponentBaseContext): ComponentBaseProps {
    const lastExecution = context.lastExecutions.length > 0 ? context.lastExecutions[0] : null;
    const componentName = context.componentDefinition.name ?? "digitalocean";

    return {
      iconSrc: doIcon,
      collapsedBackground: getBackgroundColorClass(context.componentDefinition.color),
      collapsed: context.node.isCollapsed,
      title: context.node.name || context.componentDefinition.label || "Unnamed component",
      eventSections: lastExecution ? baseEventSections(context.nodes, lastExecution, componentName) : undefined,
      metadata: metadataList(context.node),
      includeEmptyState: !lastExecution,
      eventStateMap: getStateMap(componentName),
    };
  },

  getExecutionDetails(context: ExecutionDetailsContext): Record<string, unknown> {
    const details: Record<string, string> = {};

    if (context.execution.createdAt) {
      details["Executed At"] = new Date(context.execution.createdAt).toLocaleString();
    }

    const outputs = context.execution.outputs as { default?: OutputPayload[] } | undefined;
    const database = outputs?.default?.[0]?.data as Record<string, unknown> | undefined;
    if (!database) return details;

    details["Database"] = String(database.name || "-");
    details["Cluster"] = String(database.databaseClusterName || "-");
    details["Engine"] = String(database.engine || "-");
    details["Version"] = String(database.version || "-");
    details["Region"] = String(database.region || "-");
    details["Status"] = String(database.status || "-");

    const connection = database.connection as Record<string, unknown> | undefined;
    if (connection?.host) {
      details["Host"] = String(connection.host);
    }

    return details;
  },

  subtitle(context: SubtitleContext): string | React.ReactNode {
    if (!context.execution.createdAt) return "";
    return renderTimeAgo(new Date(context.execution.createdAt));
  },
};

function metadataList(node: NodeInfo): MetadataItem[] {
  const metadata: MetadataItem[] = [];
  const nodeMetadata = node.metadata as DatabaseNodeMetadata | undefined;
  const configuration = node.configuration as GetDatabaseConfiguration;

  if (nodeMetadata?.databaseName) {
    metadata.push({ icon: "database", label: nodeMetadata.databaseName });
  } else if (configuration?.database) {
    metadata.push({ icon: "info", label: `Database: ${configuration.database}` });
  }

  if (nodeMetadata?.databaseClusterName) {
    metadata.push({ icon: "server", label: nodeMetadata.databaseClusterName });
  } else if (configuration?.databaseCluster) {
    metadata.push({ icon: "info", label: `Cluster ID: ${configuration.databaseCluster}` });
  }

  return metadata;
}
